/**
 * FOR SALE — espace équipe v2 : point d'entrée (lot L0). Seule interface de la page depuis la bascule (lot L5) : le panel
 * v1 (fs-admin.js) est retiré ; le réglage « Espace équipe v2 » reste dans le schéma de la section, sans effet.
 * La liste des vues est FIGÉE ici : chaque lot remplit son propre fichier de vue, aucun ne modifie ce module.
 * Accueil, Conversations, Annonces et À relire se chargent à leur première ouverture (import dynamique) : la coque lit
 * leur fiche (fs-admin-registry.js) pour la navigation, et seul le premier écran est téléchargé au démarrage ; les
 * autres vues permises suivent pendant les temps morts.
 *
 * Contrat de vue (architecture.md §6.2) :
 *   export const views = [{
 *     id,                     // valeur de ?ecran= (fs-admin-route.js VIEW_IDS ; ?view= lu aussi)
 *     label, icon,            // libellé de navigation (produit.md §5.1), pictogramme du sprite
 *     nav: { group: 'main' | 'admin', order, parent?, shortcut? },   // shortcut : « g a », « g c »…
 *     ready: boolean,         // false : vue non livrée, absente de la navigation
 *     requires(me) → boolean, // affichage seulement : le service revérifie chaque appel
 *     mount(container, ctx) → { update(route), unmount() } | Promise<…>,
 *   }]
 *   ctx = { account, api, store, router, poller, shortcuts, palette, drafts, prefs, returnTo, ui, intents, me, announce(text, tone) }
 *   - `container` est vide à l'appel ; la vue y rend tout son contenu et garde les attributs data-fs-* du contrat.
 *   - `update(route)` reçoit chaque changement d'adresse qui reste dans la vue (filtre, dossier, jeu).
 *   - `unmount()` arrête ses sondages (ctx.poller), abandonne ses portées (ctx.api.scope().cancel()) et retire ses
 *     écouteurs de document. La coque arrête aussi tous les sondages au changement de vue.
 */
import { createApi, createIntents, createPoller, StaleError } from './fs-admin-api.js';
import { createStore, createDrafts, createPrefs, createReturn } from './fs-admin-store.js';
import { createRouter, parseRoute } from './fs-admin-route.js';
import * as ui from './fs-admin-ui.js';
import { mountShell } from './fs-admin-shell.js';
import { VIEW_META } from './fs-admin-registry.js';
import { el, put, skeleton } from './fs-ui.js';
import { views as sellers } from './fs-admin-view-sellers.js';
import { views as payments } from './fs-admin-view-payments.js';
import { views as journal } from './fs-admin-view-journal.js';
import { views as pilot } from './fs-admin-view-pilot.js';

/* Modules des vues chargées à la demande (chemins relatifs, résolus par la carte d'import fs-admin-importmap). À relire :
   aussi l'application de relecture (fs-moderation.js), que sa vue importe avant de montrer sa racine. */
const LOADERS = Object.freeze({
  home: () => import('./fs-admin-view-home.js'),
  tickets: () => import('./fs-admin-view-tickets.js'),
  catalogue: () => import('./fs-admin-view-catalogue.js'),
  moderation: () => Promise.all([import('./fs-admin-view-catalogue.js'), import('./fs-moderation.js')]).then(([module]) => module),
});

/**
 * Vue chargée à son premier montage : squelette pendant le chargement, puis conteneur vide rendu au vrai `mount` (contrat
 * inchangé). Écran quitté entre-temps (la coque a vidé le conteneur pour un autre) : StaleError, que la coque ignore.
 */
function onDemand(id) {
  return {
    ...VIEW_META[id],
    async mount(container, ctx) {
      const waiting = el('div', { class: 'a-page-inner' }, skeleton({ rows: 6, label: 'Ouverture de l’écran' }));
      put(container, waiting);
      let loaded;
      // Écran quitté pendant le chargement : StaleError aussi en cas d'échec, l'erreur ne remplace jamais l'écran courant.
      try { loaded = await LOADERS[id](); } catch (error) { throw waiting.parentNode === container ? error : new StaleError(); }
      if (waiting.parentNode !== container) throw new StaleError();
      const view = loaded.views.find((entry) => entry.id === id);
      put(container);
      if (!view) throw new Error(`view_missing_${id}`);
      warmViews(ctx.me);
      return view.mount(container, ctx);
    },
  };
}

export const VIEWS = Object.freeze([...['home', 'tickets', 'catalogue', 'moderation'].map(onDemand), ...sellers, ...payments, ...journal, ...pilot]);

/*
 * Après le premier écran (3 s, le temps que ses lectures arrivent, puis un temps mort) : modules des autres vues permises
 * (sans effet au chargement), pour qu'elles s'ouvrent sans attente. fs-moderation.js, qui se monte en s'exécutant, est
 * seulement préchargé (téléchargé et compilé, jamais exécuté avant l'ouverture d'À relire).
 */
let warmed = false;
function warmViews(me) {
  if (warmed || !me || typeof window === 'undefined') return;
  warmed = true;
  const idle = (fn) => setTimeout(() => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 5000 }) : fn()), 3000);
  idle(() => {
    for (const view of VIEWS) {
      if (!LOADERS[view.id] || !view.ready || !view.requires(me)) continue;
      if (view.id !== 'moderation') { LOADERS[view.id]().catch(() => {}); continue; }
      LOADERS.catalogue().catch(() => {});
      if (typeof import.meta.resolve !== 'function') continue;
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = import.meta.resolve('./fs-moderation.js');
      document.head.append(link);
    }
  });
}

/** Monte l'espace v2 dans `root` ([data-fs-admin-v2]) avec la session du site (`window.FSAccount`). */
export function mountApp(root, account) {
  if (!root || !account || root.dataset.ready) return null;
  root.dataset.ready = 'true';
  const ctx = { account, ui, me: null, announce: () => {}, onAccess: () => {} };
  ctx.api = createApi(account, { onAccess: (status) => ctx.onAccess(status) });
  ctx.store = createStore({ me: null });
  ctx.router = createRouter();
  ctx.poller = createPoller({ isBusy: () => ctx.api.busy() });
  ctx.shortcuts = ui.createShortcuts({ isActive: () => ctx.router.current().view !== 'moderation' });
  ctx.palette = ui.createPalette();
  ctx.drafts = createDrafts(() => { const session = account.session(); return session?.key ?? session?.exp; });
  ctx.prefs = createPrefs();
  ctx.returnTo = createReturn();
  ctx.intents = createIntents();
  const shell = mountShell(root, ctx, VIEWS);
  const begin = () => {
    const saved = ctx.returnTo.take();
    if (saved) { const route = parseRoute(saved); ctx.router.go(route, { push: false }); }
    shell.start();
  };
  if (account.returning) account.returning.promise.then(begin); else begin();
  return ctx;
}

if (typeof document !== 'undefined') mountApp(document.querySelector('[data-fs-admin-v2]'), window.FSAccount);
